/**
 * Prepare a gasless claim operation
 */
import type { Address } from "viem"
import { Errors } from "../../errors"
import {
	deriveMarketStatus,
	type ReadableClient,
	readEmergencyPausedAt,
	readMarketInfo,
	readResolution,
	readUserPosition,
} from "../../internal/contracts"
import {
	createDeadline,
	createDeadlineDate,
	DEFAULT_DEADLINE_SECONDS,
	fromUsdcScale,
} from "../../internal/convert"
import { buildClaimSigningPayload, generateNonce } from "../../internal/signing"
import type { ClaimPreparation } from "../../types"

export interface PrepareClaimParams {
	market: Address
	user: Address
	/** Chain ID (required - get from your app config) */
	chainId: number
	/** Deadline in seconds from now (default: 300 = 5 minutes) */
	deadlineSeconds?: number
}

/**
 * Prepare a gasless claim operation
 *
 * Uses the native claimWithSignature function on the market contract.
 * User signs once, backend executes.
 *
 * @example
 * ```ts
 * import { prepareClaim } from "@breakout/sdk/actions/prepare"
 *
 * const preparation = await prepareClaim(publicClient, {
 *   market: "0x...",
 *   user: "0x...",
 *   chainId: 84532, // From your app config
 * })
 * ```
 */
export async function prepareClaim(
	client: ReadableClient,
	params: PrepareClaimParams,
): Promise<ClaimPreparation> {
	const { market, user, chainId, deadlineSeconds = DEFAULT_DEADLINE_SECONDS } = params

	// Fetch market data in parallel
	const [info, resolution, pausedAt, position] = await Promise.all([
		readMarketInfo(client, market),
		readResolution(client, market),
		readEmergencyPausedAt(client, market),
		readUserPosition(client, market, user),
	])

	// Check market status
	const status = deriveMarketStatus(
		info.status,
		info.openTimeSec,
		info.cutoffTimeSec,
		resolution,
		pausedAt,
	)

	if (status.type !== "finalized") {
		throw Errors.marketNotFinalized()
	}

	// Check user can claim
	if (position.claimed) {
		throw Errors.alreadyClaimed()
	}

	if (position.claimable === 0n) {
		throw Errors.nothingToClaim()
	}

	const claimable = fromUsdcScale(position.claimable)
	const deadline = createDeadline(deadlineSeconds)
	const expiresAt = createDeadlineDate(deadlineSeconds)

	// Generate nonce for the signature
	const nonce = generateNonce()

	// Build signing payload for native claimWithSignature
	const signingPayload = buildClaimSigningPayload(chainId, market, {
		trader: user,
		nonce,
		deadline,
	})

	return {
		type: "claim",
		signingPayload,
		claimable,
		expiresAt,
		_execution: {
			market,
			user,
			nonce,
			deadline,
		},
	}
}
